import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    ArrowRight,
    Shield,
    Lock,
    Eye,
    Server,
    FileCheck,
    UserCheck,
    CheckCircle,
    Globe,
} from 'lucide-react';

const securityFeatures = [
    {
        icon: Lock,
        title: 'SSL/TLS Encryption',
        description:
            'All data transmitted between your browser and our servers is encrypted using industry-standard 256-bit SSL/TLS encryption, ensuring your information remains private and secure at all times.',
    },
    {
        icon: Server,
        title: 'Data Protection',
        description:
            'Your personal and financial data is stored in secure, encrypted databases with multiple layers of protection. We implement strict access controls and regular security audits to safeguard your information.',
    },
    {
        icon: FileCheck,
        title: 'AML Compliance',
        description:
            `${siteConfig.brand.name} adheres to rigorous Anti-Money Laundering (AML) regulations. Our compliance framework includes transaction monitoring, suspicious activity reporting, and ongoing regulatory alignment.`,
    },
    {
        icon: UserCheck,
        title: 'KYC Verification',
        description:
            'Our Know Your Customer (KYC) process verifies the identity of every trader, protecting the integrity of our platform and ensuring a safe trading environment for all participants.',
    },
    {
        icon: Eye,
        title: 'Privacy First',
        description:
            'We never sell or share your personal data with third parties. Our privacy policy is transparent, and we collect only the information necessary to provide and improve our services.',
    },
    {
        icon: Globe,
        title: 'Regulatory Standards',
        description:
            'We operate in accordance with international financial regulations and industry best practices, maintaining the highest standards of compliance across all jurisdictions we serve.',
    },
];

const trustIndicators = [
    { label: '256-bit SSL Encryption', description: 'Bank-grade security for all connections' },
    { label: 'GDPR Compliant', description: 'Full compliance with data protection regulations' },
    { label: 'Regular Security Audits', description: 'Independent third-party vulnerability assessments' },
    { label: 'Two-Factor Authentication', description: 'Optional 2FA for enhanced account security' },
    { label: 'DDoS Protection', description: 'Enterprise-grade protection against attacks' },
    { label: 'Secure Payment Processing', description: 'PCI-DSS compliant payment infrastructure' },
];

export default function Security() {
    return (
        <AppLayout title={`Security - ${siteConfig.brand.name}`} description={`Learn about the security measures and compliance standards that protect your data at ${siteConfig.brand.name}.`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Security</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
                            Your <span className="text-gradient">Security</span> is Our Priority
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Your security is our top priority. We employ industry-leading measures to protect your data,
                            your funds, and your trading activity at every level.
                        </p>
                    </FadeIn>
                </div>
            </Section>

            {/* Security Features */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="How We Protect"
                        highlight="You"
                        description="Multiple layers of security and compliance ensure a safe and trustworthy trading environment."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {securityFeatures.map((feature) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div key={feature.title} variants={staggerItem}>
                                    <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-300 p-0 h-full">
                                        <CardContent className="p-6 pt-6">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/10 mb-4">
                                                <Icon className="h-6 w-6 text-primary" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                                {feature.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                {feature.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Trust Indicators */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Trust"
                        highlight="Indicators"
                        description="The standards and certifications that demonstrate our commitment to your security."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-4">
                        {trustIndicators.map((item) => (
                            <motion.div key={item.label} variants={staggerItem}>
                                <div className="flex items-start gap-4 rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-300 p-5">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                        <CheckCircle className="h-4 w-4 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground text-sm">{item.label}</h3>
                                        <p className="text-sm text-muted-foreground mt-0.5">{item.description}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Commitment */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="max-w-4xl mx-auto">
                        <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm p-0">
                            <CardContent className="p-8 md:p-12 pt-8 md:pt-12">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                        <Shield className="h-7 w-7 text-primary" />
                                    </div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                                        Our Security Commitment
                                    </h2>
                                </div>
                                <div className="space-y-4 text-muted-foreground leading-relaxed">
                                    <p>
                                        At {siteConfig.brand.name}, security is not an afterthought — it is embedded in every aspect of our
                                        platform. From the moment you create an account to every trade you execute, we ensure
                                        your data and funds are protected by the highest industry standards.
                                    </p>
                                    <p>
                                        Our dedicated security team continuously monitors for threats, performs regular
                                        penetration testing, and stays ahead of emerging vulnerabilities. We work with
                                        leading cybersecurity firms to maintain an infrastructure you can trust.
                                    </p>
                                    <p>
                                        If you have any security concerns or questions about our practices, our team is
                                        always available to assist you.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </FadeIn>
            </Section>

            {/* CTA */}
            <Section className="bg-background">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                            Trade with <span className="text-gradient">Confidence</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                            Your security is guaranteed. Start your trading journey with a firm that takes protection
                            as seriously as performance.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Started
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </a>
                            <Link to="/contact">
                                <Button variant="outline" size="lg">
                                    Contact Us
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
